import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './Policies.css';

const Legal: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'privacy' | 'terms' | 'cookies'>('privacy');

  useEffect(() => {
    const tab = searchParams.get('tab');
    const currentPath = window.location.pathname;

    if (tab === 'privacy' || tab === 'terms' || tab === 'cookies') {
      setActiveTab(tab);
    } else if (currentPath === '/privacy') {
      setActiveTab('privacy');
    } else if (currentPath === '/terms') {
      setActiveTab('terms');
    } else if (currentPath === '/cookies') {
      setActiveTab('cookies');
    }
  }, [searchParams]);

  return (
    <div className="policies">
      <div className="policies-header">
        <div className="policy-tabs">
          <button
            className={`policy-tab ${activeTab === 'privacy' ? 'active' : ''}`}
            onClick={() => setActiveTab('privacy')}
          >
            <span>PRIVACY POLICY</span>
          </button>
          <button
            className={`policy-tab ${activeTab === 'terms' ? 'active' : ''}`}
            onClick={() => setActiveTab('terms')}
          >
            <span>TERMS OF USE</span>
          </button>
          <button
            className={`policy-tab ${activeTab === 'cookies' ? 'active' : ''}`}
            onClick={() => setActiveTab('cookies')}
          >
            <span>COOKIES</span>
          </button>
        </div>
      </div>

      <div className="policy-content">
        {activeTab === 'privacy' && (
          <div className="policy-info">
            <div className="policy-header">
              <h1>PRIVACY POLICY</h1>
            </div>

            <section className="policy-section">
              <h2>WHO WE ARE</h2>
              <p>EcoFashion, based at Vasileos Irakleiou 28, Thessaloniki, Greece, is the data controller responsible for your personal data under the EU General Data Protection Regulation (GDPR). For anything privacy-related, contact us at <a href="mailto:eco.fashion.living@gmail.com">eco.fashion.living@gmail.com</a>.</p>
            </section>

            <section className="policy-section">
              <h2>WHAT DATA WE COLLECT</h2>
              <ul className="note-list">
                <li><strong>Account data:</strong> name and email you register with. Your password is stored as a one-way hash — we never see or keep it in plain text.</li>
                <li><strong>Order & shipping data:</strong> full name, address, city, postal code, country, phone number and email, so we can deliver your order.</li>
                <li><strong>Payment data:</strong> handled directly by our payment provider at checkout — we never see or store your full card details.</li>
                <li><strong>Contact & Instagram messages:</strong> whatever you send us when you get in touch.</li>
                <li><strong>Guest cart & favorites:</strong> kept only in your own browser until you check out or log in — see our Cookies page for details.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>WHY WE PROCESS IT</h2>
              <ul className="note-list">
                <li>To create and manage your account, and to process and deliver your orders (performance of a contract).</li>
                <li>To reply to your questions or requests (legitimate interest).</li>
                <li>To keep sales records as required by Greek tax and accounting law (legal obligation).</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>WHO WE SHARE IT WITH</h2>
              <p>We share only what's needed to run your order, with:</p>
              <ul className="note-list">
                <li>our payment provider, to process card payments securely;</li>
                <li>Box Now / ELTA, to deliver your order;</li>
                <li>our cloud storage provider, where product photos are hosted.</li>
              </ul>
              <p>We do not sell your data, and we do not use it for advertising or share it with marketing companies.</p>
            </section>

            <section className="policy-section">
              <h2>HOW LONG WE KEEP IT</h2>
              <p>Account data is kept for as long as your account is active. Order and invoice data is kept for as long as Greek tax law requires us to retain sales records. You can ask us to delete your account at any time (see "Your Rights" below), and we'll remove what we're not legally required to keep.</p>
            </section>

            <section className="policy-section">
              <h2>YOUR RIGHTS</h2>
              <p>Under the GDPR, you can ask us to: give you access to the data we hold about you; correct it; delete it; restrict or object to how we use it; or give you a copy in a portable format. Email <a href="mailto:eco.fashion.living@gmail.com">eco.fashion.living@gmail.com</a> and we'll respond within 30 days.</p>
              <p>If you're not happy with our response, you can complain to the Hellenic Data Protection Authority (Αρχή Προστασίας Δεδομένων Προσωπικού Χαρακτήρα) at <a href="https://www.dpa.gr" target="_blank" rel="noopener noreferrer">www.dpa.gr</a>.</p>
            </section>

            <section className="policy-section">
              <h2>SECURITY & CHANGES</h2>
              <p>We use industry-standard measures to protect your data, including password hashing and encrypted connections (HTTPS). We may update this policy from time to time — the "Last updated" date above will always reflect the latest version.</p>
            </section>
          </div>
        )}

        {activeTab === 'terms' && (
          <div className="policy-info">
            <div className="policy-header">
              <h1>TERMS OF USE</h1>
            </div>

            <section className="policy-section">
              <h2>ACCEPTANCE OF TERMS</h2>
              <p>By browsing or buying from EcoFashion, you agree to these Terms of Use. If you don't agree with them, please don't use the site.</p>
            </section>

            <section className="policy-section">
              <h2>ABOUT OUR PRODUCTS</h2>
              <p>We sell curated vintage, deadstock and reworked fashion pieces. Because most items are second-hand or one of a kind, small variations, wear or imperfections consistent with their age are normal and part of their character — we describe each piece as accurately as we can, but exact match to photos (screen colors, lighting) can't be guaranteed.</p>
            </section>

            <section className="policy-section">
              <h2>ACCOUNTS</h2>
              <p>You're responsible for keeping your account details accurate and your password secure. Let us know right away if you think someone else has access to your account.</p>
            </section>

            <section className="policy-section">
              <h2>ORDERS & PRICING</h2>
              <p>Prices are shown in EUR and may change without notice. We reserve the right to refuse or cancel an order — for example if an item turns out to be out of stock, if there's a pricing error, or if we suspect fraud. If that happens, we'll let you know and refund any payment already made.</p>
            </section>

            <section className="policy-section">
              <h2>PAYMENT, SHIPPING & EXCHANGES</h2>
              <p>Payments are processed securely through our payment provider. For delivery times and costs, see our <a href="/shipping">Shipping Policy</a>. For returns, see our <a href="/policies?tab=exchanges">Exchanges Policy</a> — we offer exchanges within 10 business days, not cash refunds.</p>
            </section>

            <section className="policy-section">
              <h2>INTELLECTUAL PROPERTY</h2>
              <p>All site content — text, photos, logo and design — belongs to EcoFashion unless stated otherwise. Please don't copy or reuse it without our permission.</p>
            </section>

            <section className="policy-section">
              <h2>ACCEPTABLE USE</h2>
              <p>Please don't misuse the site — no scraping, no attempts to disrupt or gain unauthorized access, and no fraudulent orders.</p>
            </section>

            <section className="policy-section">
              <h2>LIABILITY & GOVERNING LAW</h2>
              <p>The site is provided "as is." We're not liable for indirect damages or for delays caused by couriers outside our control. These Terms are governed by Greek law, and any dispute falls under the courts of Thessaloniki. We may update these Terms occasionally — the "Last updated" date above reflects the latest version.</p>
            </section>
          </div>
        )}

        {activeTab === 'cookies' && (
          <div className="policy-info">
            <div className="policy-header">
              <h1>COOKIES</h1>
            </div>

            <section className="policy-section">
              <h2>DO WE USE COOKIES?</h2>
              <p>No. EcoFashion doesn't set tracking or advertising cookies, and we don't run analytics or ad-tracking scripts on this site.</p>
            </section>

            <section className="policy-section">
              <h2>WHAT WE DO USE</h2>
              <p>We use your browser's local storage — a similar but separate technology to cookies — strictly to make the site work:</p>
              <ul className="note-list">
                <li>to keep you logged in between visits;</li>
                <li>to remember your cart and favorites if you're browsing without an account.</li>
              </ul>
              <p>This data stays only on your own device, is never sent anywhere except our own servers when needed (e.g. to sync your cart to your account), and is never used to track you across other websites.</p>
            </section>

            <section className="policy-section">
              <h2>THIRD-PARTY COOKIES</h2>
              <p>We currently don't use Google Analytics, Facebook Pixel, or any similar third-party tracking tool. If that ever changes, we'll update this page and ask for your consent first, as required by law.</p>
            </section>

            <section className="policy-section">
              <h2>HOW TO CLEAR THIS DATA</h2>
              <p>Logging out clears your session. You can also clear all locally stored data at any time from your browser's settings.</p>
            </section>
          </div>
        )}

        <div className="policy-contact">
          <h2>QUESTIONS?</h2>
          <p>We're here to help!</p>
          <p>Contact us at <a href="mailto:eco.fashion.living@gmail.com">eco.fashion.living@gmail.com</a> or DM us on Instagram <a href="https://instagram.com/eco.fashion.living" target="_blank" rel="noopener noreferrer">@eco.fashion.living</a> ♡</p>
        </div>
      </div>
    </div>
  );
};

export default Legal;
